import React, {MutableRefObject, useEffect, useRef, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Dropdown} from '@components/common/dropdown/dropdown'
import {globals} from '@extensions/globals-base'
import {Icon} from '../base/icon'
import {InputNew} from '../base/input-new'
import {TablerIcons} from '../icon-tabler/icon-tabler'

const TwSuggestItem = tw.div`
  px-12
  py-6
  cursor-pointer
  text-primary
  hover:bg-hover
  w-full
  min-w-0

  hover:bg-background-hover
  active:bg-background-active
`

export interface SuggestItem {
  id: string
  title: string
  isNew?: boolean
}

interface InputWithSuggestProps {
  value: string
  onChange: (value: string) => void
  onSelect?: (item: SuggestItem) => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  items: SuggestItem[]
  suggestionsLimit?: number
  placeholder?: string
  className?: string
  inputClassName?: string
  iconLeft?: TablerIcons
  iconRight?: TablerIcons
  disabled?: boolean
  hasError?: boolean
  caption?: string
  getAddAsNewLabel?: (value: string) => string
  addAsNewButtonBehavior?: 'auto' | 'always'
  inputRef?: MutableRefObject<HTMLInputElement>
}

const getFilteredSuggestions = (
  items: SuggestItem[],
  query: string,
  suggestionsLimit: number
) => {
  if (query.length === 0) {
    return []
  }

  const locale = globals.lang

  return items
    .filter(item =>
      item.title
        .toLocaleLowerCase(locale)
        .includes(query.toLocaleLowerCase(locale))
    )
    .sort((a, b) => {
      const aTitle = a.title.toLocaleLowerCase(locale)
      const bTitle = b.title.toLocaleLowerCase(locale)
      const q = query.toLocaleLowerCase(locale)

      const aIndex = aTitle.indexOf(q)
      const bIndex = bTitle.indexOf(q)

      if (aIndex !== bIndex) {
        return aIndex - bIndex
      }

      return a.title.localeCompare(b.title, locale)
    })
    .slice(0, suggestionsLimit)
}

export const InputWithSuggest = ({
  value,
  onChange,
  onSelect,
  onKeyDown,
  items,
  suggestionsLimit = 6,
  placeholder,
  className,
  inputClassName,
  iconLeft,
  iconRight,
  disabled,
  hasError,
  caption,
  getAddAsNewLabel,
  addAsNewButtonBehavior = 'auto',
  inputRef
}: InputWithSuggestProps) => {
  const [isSuggestionsVisible, setIsSuggestionsVisible] = useState(false)
  const [filteredItems, setFilteredItems] = useState(items)
  const suggestInputRef = inputRef ?? useRef<HTMLInputElement>()
  const initialValueRef = useRef<string | undefined>()
  const locale = globals.lang

  const suggest = () => {
    const query = value.trim().toLocaleLowerCase(locale)
    const filtered = getFilteredSuggestions(items, query, suggestionsLimit)

    setFilteredItems(filtered)

    setIsSuggestionsVisible(
      (query.length > 0 && filtered.length > 0) ||
        addAsNewButtonBehavior === 'always'
    )
  }

  useEffect(() => {
    initialValueRef.current = value
  }, [])

  useEffect(() => {
    if (document.activeElement !== suggestInputRef?.current) {
      return
    }

    const query = value.trim().toLocaleLowerCase(locale)

    // NOTE: Here we avoid display filtered items on first mount
    if (query.length > 0 && initialValueRef?.current === value) {
      return
    }

    suggest()
  }, [value, items])

  const handleOnFocus = () => {
    if (
      (value.trim().length > 0 && filteredItems.length > 0) ||
      addAsNewButtonBehavior === 'always'
    ) {
      suggest()
    }
  }

  const handleSelect = (item: SuggestItem) => {
    suggestInputRef.current?.blur()
    setIsSuggestionsVisible(false)
    onChange(item.title)
    onSelect?.(item)
  }

  const handleAddNew = () => {
    suggestInputRef.current?.blur()
    setIsSuggestionsVisible(false)
    const newItem = {
      ...filteredItems[0],
      title: value,
      isNew: true
    }
    onChange(value)
    onSelect?.(newItem)
  }

  const addAsNewButtonText =
    getAddAsNewLabel?.(value) ?? Lang.msg.addAsNewLabel(value)

  // TODO: support Arrows up/down + Tab + Shift Tab + Enter
  const dropdownContent = (
    <div
      className="shadow-lg overflow-hidden rounded-8 py-8"
      // NOTE: prevents unfocus the input (to avoid firing the onBlur callback)
      onMouseDown={e => e.preventDefault()}
    >
      {filteredItems.map((item, index) => {
        const label = item.title
        const lowerLabel = label.toLowerCase()
        const lowerValue = value.toLowerCase()
        const matchIndex = lowerLabel.indexOf(lowerValue)

        let before = label,
          match = '',
          after = ''

        if (matchIndex !== -1 && value) {
          before = label.slice(0, matchIndex)
          match = label.slice(matchIndex, matchIndex + value.length)
          after = label.slice(matchIndex + value.length)
        }

        return (
          <TwSuggestItem key={index} onClick={() => handleSelect(item)}>
            {before}
            {match && <span className="text-action-default">{match}</span>}
            {after}
          </TwSuggestItem>
        )
      })}
      {value.trim().length > 0 && (
        <div
          className={
            filteredItems.length > 0
              ? 'shadow-border-t-1 shadow-line-base pt-8 mt-8'
              : ''
          }
        >
          <TwSuggestItem
            className="flex gap-8 items-center"
            onClick={e => {
              e.stopPropagation()
              handleAddNew()
            }}
          >
            <Icon icon="plus" color="primary" />
            {addAsNewButtonText}
          </TwSuggestItem>
        </div>
      )}
    </div>
  )

  return (
    <Dropdown
      visible={isSuggestionsVisible}
      onVisibleChange={setIsSuggestionsVisible}
      content={dropdownContent}
      autoRelayout
      hideOverlay
      hideArrow
    >
      <InputNew
        ref={suggestInputRef}
        className={className}
        inputClassName={inputClassName}
        value={value}
        onChange={onChange}
        onBlur={() => {
          setIsSuggestionsVisible(false)
        }}
        onFocus={handleOnFocus}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        iconLeft={iconLeft}
        iconRight={iconRight}
        disabled={disabled}
        error={hasError}
        support={caption}
      />
    </Dropdown>
  )
}
